import { useState, useEffect, useRef } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { supabase } from '@/lib/supabase';

export default function ResetPasswordPage() {
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [checking, setChecking] = useState(true);
  const navigate = useNavigate();
  const location = useLocation();
  const sessionChecked = useRef(false);

  useEffect(() => {
    if (sessionChecked.current) return;
    sessionChecked.current = true;

    let stale = false;

    const checkForSession = async () => {
      // First, try getSession — Supabase might have already parsed the hash
      const { data: { session } } = await supabase.auth.getSession();
      if (stale) return;

      if (session) {
        setChecking(false);
        return;
      }

      // No session yet. Listen for PASSWORD_RECOVERY or SIGNED_IN from hash processing
      const { data: listener } = supabase.auth.onAuthStateChange((event, newSession) => {
        if (stale) return;

        if ((event === 'PASSWORD_RECOVERY' || event === 'SIGNED_IN') && newSession) {
          setChecking(false);
        }
      });

      // Safety timeout — if nothing happens in 4 seconds, show error
      const timeout = setTimeout(() => {
        if (stale) return;
        setError('Link inválido ou expirado. Solicite uma nova recuperação de senha.');
        setChecking(false);
      }, 4000);

      return () => {
        clearTimeout(timeout);
        listener.subscription.unsubscribe();
      };
    };

    checkForSession();

    return () => {
      stale = true;
    };
  }, [location.hash]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (password.length < 6) {
      setError('A senha deve ter pelo menos 6 caracteres.');
      return;
    }

    if (password !== confirmPassword) {
      setError('As senhas não coincidem.');
      return;
    }

    setLoading(true);

    const { error: updateError } = await supabase.auth.updateUser({
      password,
    });

    if (updateError) {
      setError(updateError.message);
      setLoading(false);
      return;
    }

    setSuccess(true);
    setLoading(false);

    setTimeout(() => {
      navigate('/login', { replace: true });
    }, 2500);
  };

  if (checking) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background-50">
        <div className="flex flex-col items-center gap-4">
          <div className="w-10 h-10 border-[3px] border-primary-500 border-t-transparent rounded-full animate-spin" />
          <p className="text-sm text-foreground-500">Verificando link de recuperação...</p>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background-50 px-5">
        <div className="absolute top-0 right-0 w-[300px] h-[300px] rounded-full bg-primary-100/40 blur-[120px] pointer-events-none" />
        <div className="absolute bottom-0 left-0 w-[200px] h-[200px] rounded-full bg-accent-100/30 blur-[100px] pointer-events-none" />

        <div className="w-full max-w-[400px] relative z-10 text-center animate-scaleIn">
          <div className="w-16 h-16 mx-auto mb-6 rounded-2xl bg-emerald-50 dark:bg-emerald-500/10 flex items-center justify-center">
            <i className="ri-shield-check-line text-3xl text-emerald-500" />
          </div>
          <h2 className="text-2xl font-heading font-bold text-foreground-950 dark:text-foreground-100 mb-3">
            Senha atualizada!
          </h2>
          <p className="text-sm text-foreground-500 dark:text-foreground-400 mb-8 leading-relaxed">
            Sua senha foi redefinida com sucesso. Você será redirecionado para o login em instantes.
          </p>
          <Link
            to="/login"
            className="inline-flex items-center gap-2 text-sm font-semibold text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 transition-colors"
          >
            <i className="ri-arrow-left-line" />
            Ir para o login
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-background-50 px-5">
      <div className="absolute top-0 right-0 w-[300px] h-[300px] rounded-full bg-primary-100/40 blur-[120px] pointer-events-none" />
      <div className="absolute bottom-0 left-0 w-[200px] h-[200px] rounded-full bg-accent-100/30 blur-[100px] pointer-events-none" />

      <div className="w-full max-w-[420px] relative z-10">
        <div className="flex items-center gap-3 mb-10 justify-center">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-primary-500 to-primary-600 flex items-center justify-center">
            <i className="ri-whatsapp-fill text-white text-xl" />
          </div>
          <span className="text-xl font-heading font-bold text-foreground-950 dark:text-foreground-100">WZap PRO</span>
        </div>

        <div className="mb-8 text-center">
          <h2 className="text-3xl font-heading font-bold text-foreground-950 dark:text-foreground-100 mb-2">
            Redefinir senha
          </h2>
          <p className="text-sm text-foreground-500 dark:text-foreground-400">
            Digite sua nova senha abaixo.
          </p>
        </div>

        {error && (
          <div className="mb-5 bg-red-50 dark:bg-red-500/10 border border-red-200 dark:border-red-500/20 rounded-xl px-4 py-3 flex items-center gap-3 animate-fadeIn">
            <i className="ri-error-warning-line text-red-500 text-lg flex-shrink-0" />
            <p className="text-sm text-red-700 dark:text-red-400">{error}</p>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-5">
          <div>
            <label htmlFor="password" className="block text-sm font-medium text-foreground-700 dark:text-foreground-300 mb-1.5">
              Nova senha
            </label>
            <div className="relative">
              <i className="ri-lock-line absolute left-3.5 top-1/2 -translate-y-1/2 text-foreground-400 text-lg" />
              <input
                id="password"
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full pl-11 pr-11 py-3 bg-white dark:bg-background-100 border border-background-200 dark:border-background-300 rounded-xl text-sm text-foreground-900 dark:text-foreground-100 placeholder:text-foreground-400 outline-none focus:border-primary-400 focus:ring-4 focus:ring-primary-100/50 transition-all"
                placeholder="Mínimo 6 caracteres"
                required
                minLength={6}
                autoFocus
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3.5 top-1/2 -translate-y-1/2 text-foreground-400 hover:text-foreground-600 dark:hover:text-foreground-300 transition-colors cursor-pointer w-8 h-8 flex items-center justify-center rounded-lg hover:bg-background-100 dark:hover:bg-background-200"
              >
                <i className={`text-lg ${showPassword ? 'ri-eye-off-line' : 'ri-eye-line'}`} />
              </button>
            </div>
          </div>

          <div>
            <label htmlFor="confirmPassword" className="block text-sm font-medium text-foreground-700 dark:text-foreground-300 mb-1.5">
              Confirmar senha
            </label>
            <div className="relative">
              <i className="ri-lock-line absolute left-3.5 top-1/2 -translate-y-1/2 text-foreground-400 text-lg" />
              <input
                id="confirmPassword"
                type={showPassword ? 'text' : 'password'}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="w-full pl-11 pr-4 py-3 bg-white dark:bg-background-100 border border-background-200 dark:border-background-300 rounded-xl text-sm text-foreground-900 dark:text-foreground-100 placeholder:text-foreground-400 outline-none focus:border-primary-400 focus:ring-4 focus:ring-primary-100/50 transition-all"
                placeholder="Repita a nova senha"
                required
              />
            </div>
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full py-3 bg-primary-600 hover:bg-primary-700 disabled:bg-primary-400 text-white rounded-xl text-sm font-semibold transition-all duration-200 flex items-center justify-center gap-2 whitespace-nowrap cursor-pointer shadow-sm hover:shadow-md active:scale-[0.98]"
          >
            {loading ? (
              <>
                <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                <span>Salvando...</span>
              </>
            ) : (
              <>
                <i className="ri-save-line text-lg" />
                <span>Salvar nova senha</span>
              </>
            )}
          </button>
        </form>

        <p className="mt-8 text-center text-sm text-foreground-500 dark:text-foreground-400">
          <Link
            to="/login"
            className="inline-flex items-center gap-1.5 text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 font-semibold transition-colors"
          >
            <i className="ri-arrow-left-line" />
            Voltar para o login
          </Link>
        </p>
      </div>
    </div>
  );
}